/**
 * `storeCollections.*` — Store Admin collections (Commerce, M6).
 *
 * A collection is a **line sheet**: `core` is the always-orderable catalog,
 * `seasonal` a dated drop. Membership is N:N via `store_collection_listings`,
 * and a listing carries its own position in each sheet it belongs to.
 *
 * `phase` is **derived** from the type and season window on every read and is
 * never stored — a stored label would still read "live" months after the window
 * shut. It is also the orderability contract (spec 15 R3): `upcoming` =
 * pre-book, `live` = at-once, `ended` = hidden from buyers. `status` carries
 * only the manual archive dimension.
 *
 * @see packages/domain/src/commerce/store · docs/spec/15-store-collections.md
 */
import { z } from "zod";
import { uuid, paged } from "../_common.js";

/** Spec 15 R1. `evergreen`/`themed`/`smart` were mapped to `core` by 0142. */
export const collectionType = z.enum(["core", "seasonal"]);
export type CollectionType = z.infer<typeof collectionType>;

export const collectionStatus = z.enum(["active", "archived"]);
export const collectionPhase = z.enum(["upcoming", "live", "ended", "archived"]);
export type CollectionPhase = z.infer<typeof collectionPhase>;

/** Shared shape of the details an operator types. */
const collectionDetails = {
  name: z.string().trim().min(1, "Name is required").max(120),
  type: collectionType,
  description: z.string().trim().max(2000).nullable().optional(),
  /** Cover art URL for the storefront tile. */
  imageUrl: z.string().trim().max(2048).nullable().optional(),
  /** Season window — required for `seasonal`, always null for `core` (R1). */
  activeFrom: z.string().datetime().nullable().optional(),
  activeTo: z.string().datetime().nullable().optional(),
};

export const collectionRow = z.object({
  collectionId: uuid,
  name: z.string(),
  type: collectionType,
  description: z.string().nullable(),
  imageUrl: z.string().nullable(),
  activeFrom: z.string().datetime().nullable(),
  activeTo: z.string().datetime().nullable(),
  status: collectionStatus,
  phase: collectionPhase,
  /** Members in this collection. Computed per row — a collection may hold none. */
  productCount: z.number().int().nonnegative(),
  createdAt: z.string().datetime(),
});

/** One product inside a collection, joined to its item for name/SKU/price. */
export const collectionMember = z.object({
  listingId: uuid,
  name: z.string(),
  sku: z.string().nullable(),
  basePricePaise: z.string().nullable(),
  visibility: z.enum(["draft", "published", "archived"]),
  images: z.array(z.string()),
  /** Line-sheet position — contiguous from 1 within this collection (R5). */
  sortOrder: z.number().int(),
});

// ── list ──────────────────────────────────────────────────────────
export const listInput = z.object({
  phase: z.enum(["all", "live", "upcoming", "ended", "archived"]).default("all"),
  cursor: z.string().optional(),
  limit: z.number().int().min(1).max(100).default(50),
  q: z.string().optional(),
  /** Archived are hidden by default, the way the products `all` tab hides them. */
  includeArchived: z.boolean().default(false),
});
export const listOutput = paged(collectionRow);

// ── get ───────────────────────────────────────────────────────────
export const getInput = z.object({ collectionId: uuid });
export const getOutput = collectionRow.extend({ members: z.array(collectionMember) });

// ── create / update ───────────────────────────────────────────────
export const createInput = z.object(collectionDetails);
export const createOutput = z.object({ collectionId: uuid });

/**
 * `type` stays changeable here even though the Edit screen locks the picker.
 *
 * Deliberate, and API-only. Spec 15 defines no type-change rule, so removing the
 * field would be inventing one; the form locks it because *design* 17 says a Core
 * catalog isn't a season that forgot its dates. The write stays safe either way —
 * `assertCollectionShape` validates the **post-patch** shape, so switching to
 * `core` drops the window and switching to `seasonal` demands one.
 */
export const updateInput = z
  .object({ collectionId: uuid })
  .extend({ ...collectionDetails, name: collectionDetails.name.optional(), type: collectionType.optional() });
export const updateOutput = z.object({ collectionId: uuid });

// ── archive / restore ─────────────────────────────────────────────
export const archiveInput = z.object({ collectionId: uuid });
export const archiveOutput = z.object({ collectionId: uuid, status: collectionStatus });

// ── membership ────────────────────────────────────────────────────
export const addListingsInput = z.object({
  collectionId: uuid,
  listingIds: z.array(uuid).min(1).max(200),
});
export const removeListingInput = z.object({ collectionId: uuid, listingId: uuid });
export const membershipOutput = z.object({
  collectionId: uuid,
  productCount: z.number().int().nonnegative(),
});

/**
 * The whole line sheet in its new order, first to last (R5).
 *
 * The full list rather than a move: the screen already holds it, and a
 * "move X to 3" would need the same starting state to replay against.
 *
 * The domain tolerates a *partial* list — ids it doesn't recognise are dropped
 * and members you didn't mention keep their order behind the ones you did — but
 * the admin screen always sends every member, so a collection past this cap
 * would fail the parse on every reorder rather than degrade. 1000 styles in one
 * line sheet is well beyond what a season contains; revisit with pagination on
 * the detail screen, not by raising this number alone.
 */
export const reorderInput = z.object({
  collectionId: uuid,
  listingIds: z.array(uuid).min(1).max(1000),
});

export type CreateCollectionInput = z.infer<typeof createInput>;
export type UpdateCollectionInput = z.infer<typeof updateInput>;

// ── performance (rail card) ───────────────────────────────────────
/**
 * What the styles **currently in** a line sheet have sold.
 *
 * A separate query rather than a field on `get`, for the same reason `counts` is
 * separate from `list`: the detail page paints without waiting on an order-lines
 * aggregate, and the card refreshes on its own.
 *
 * Three properties of the number the screen must state rather than imply — it
 * follows current membership (not the sheet's history), it double-counts a style
 * that sits in two sheets, and `packs` is the unit of sale, never pieces.
 */
export const performanceInput = z.object({ collectionId: uuid });
export const performanceOutput = z.object({
  /** Sum of line totals, net of line discount and **excluding tax**. Paise, as a string. */
  revenuePaise: z.string(),
  /** Units of sale. Packs — not pieces. */
  packs: z.number().int().nonnegative(),
  /** Packs × the pack's piece count; legacy pack-less lines count 1:1. */
  pieces: z.number().int().nonnegative(),
  /** Distinct orders, so one order holding two of the sheet's styles counts once. */
  orderCount: z.number().int().nonnegative(),
  productCount: z.number().int().nonnegative(),
});

// ── counts (tab badges) ───────────────────────────────────────────
/**
 * Per-phase totals. Separate from `list` so the badges refresh after a
 * mutation without re-paginating. `all` excludes archived, matching the tab.
 */
export const countsOutput = z.object({
  all: z.number().int().nonnegative(),
  live: z.number().int().nonnegative(),
  upcoming: z.number().int().nonnegative(),
  ended: z.number().int().nonnegative(),
  archived: z.number().int().nonnegative(),
});

// ── duplicate ─────────────────────────────────────────────────────
/**
 * Clones details + membership + order under a new name.
 *
 * The source's window is never inherited — a copy is a *new* season, and last
 * season's dates would make it read "Ended" on creation. But R1 gives a
 * seasonal collection no meaning without a window, so the copy's own dates come
 * in here. A core source ignores them.
 */
export const duplicateInput = z.object({
  collectionId: uuid,
  name: z.string().trim().min(1, "Name is required").max(120),
  activeFrom: z.string().datetime().nullable().optional(),
  activeTo: z.string().datetime().nullable().optional(),
});
export const duplicateOutput = z.object({ collectionId: uuid });
